"use client";
import { Cache, makeVar, Reference, StoreObject } from "@apollo/client/cache";
import { SetContextLink } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import { HttpLink } from "@apollo/client/link/http";
import { useApolloClient } from "@apollo/client/react";
import { SentryLink, SentryLinkOptions } from "apollo-link-sentry";

import { ClientFacingErrors } from "@portal/app/_common/types/clientErrors";
import {
  COOKIE_KEYS,
  deleteCookie,
  getCookie,
  HEADER_KEYS,
  setCookie,
} from "@portal/app/_common/lib/headers";
import { errorToast } from "./context/ToastProvider";
import { detectKnownClientError } from "@portal/app/_common/lib/clientErrorUtils";
import { externalUrls, getUrls } from "@portal/app/_common/lib/urls";
import { ApolloClient, InMemoryCache } from "@apollo/client-integration-nextjs";
import { isProduction } from "@portal/app/_common/lib/edgeUtils";
import { logger as rootLogger } from "@portal/app/_common/lib/logger";
import { decodeJwt } from "@portal/app/_common/lib/token";
import { resolveSsrAttestation } from "@portal/app/_common/lib/ssrAttestation";
import { serverLogout } from "@portal/app/_server/actions/logout";
import { ApolloLink } from "@apollo/client/link";
import { map } from "rxjs";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { loadErrorMessages, loadDevMessages } from "@apollo/client/dev";

const logger = rootLogger.child({ module: "apolloUtils" });

if (process.env.NODE_ENV !== "production") {
  loadDevMessages();
  loadErrorMessages();
}

export const accessTokenVar = makeVar(getCookie(COOKIE_KEYS.ACCESS_TOKEN));
export const refreshTokenVar = makeVar(getCookie(COOKIE_KEYS.REFRESH_TOKEN));

const graphqlUrl =
  typeof window !== "undefined"
    ? `${window.location.origin}/api/graphql`
    : `${process.env.DREAM_PORTAL_URL}/api/graphql`;
const graphqlUrlInternal = process.env.DREAM_INTERNAL_PORTAL_URL
  ? `${process.env.DREAM_INTERNAL_PORTAL_URL}:${process.env.PORT}/api/graphql`
  : `${process.env.DREAM_PORTAL_URL}/api/graphql`;

// Keep operation names and safe errors in both browser and server breadcrumbs.
// GraphQL results/variables can contain access, refresh and login credentials;
// retaining full payloads also pins large responses during SSR (ENG-7891).
const attachBreadcrumbs: SentryLinkOptions["attachBreadcrumbs"] = {
  includeError: true,
  // Error objects in v4 can retain partial GraphQL data or HTTP bodies.
  // Keep diagnostic metadata, never the response or the full result.
  transform: (breadcrumb) => {
    const error = breadcrumb.data.error;
    if (error && typeof error === "object") {
      breadcrumb.data.error = {
        name: error.name,
        message: error.message,
        ...("statusCode" in error &&
          typeof error.statusCode === "number" && { statusCode: error.statusCode }),
        ...(CombinedGraphQLErrors.is(error) && {
          errors: error.errors.map(({ message, path, extensions }) => ({
            message,
            path,
            extensions: { code: extensions?.code },
          })),
        }),
      } as unknown as typeof error;
    }
    return breadcrumb;
  },
};
// Keep operation names off the shared Sentry scope (ENG-13193): the library
// default makes them the transaction and fingerprint of every later error on
// the page, GraphQL or not. Breadcrumbs still record each operation.
const sentryGraphqlHelperLink = new SentryLink({
  uri: graphqlUrl,
  attachBreadcrumbs,
  setTransaction: false,
  setFingerprint: false,
});
const sentryGraphqlInternalHelperLink = new SentryLink({
  uri: graphqlUrlInternal,
  attachBreadcrumbs,
  setTransaction: false,
  setFingerprint: false,
});

const responseLink = new ApolloLink((operation, forward) => {
  return forward(operation).pipe(
    map((response) => {
      const context = operation.getContext();
      const {
        response: { headers },
      } = context;

      const maybeAccessToken = headers.get(HEADER_KEYS.AUTHORIZATION)?.split(" ")[1];
      if (maybeAccessToken) {
        updateTokens({ accessToken: maybeAccessToken });
      }
      const maybeRefreshToken = headers.get(HEADER_KEYS.REFRESH_TOKEN);
      if (maybeRefreshToken) {
        updateTokens({ refreshToken: maybeRefreshToken });
      }

      return response;
    })
  );
});

const getUrlWithOperation = (uri: URL, body: BodyInit | null | undefined) => {
  let operationName: string | undefined;
  if (body) {
    try {
      const bodyObj = JSON.parse(body.toString());
      if (bodyObj.operationName) {
        operationName = bodyObj.operationName;
      }
    } catch (err) {
      logger.error(err);
    }
  }
  return `${uri}?${operationName}`;
};

const httpLink = new HttpLink({
  uri: graphqlUrl,
  fetchOptions: { cache: "no-store" },
  fetch: (url, options) => {
    try {
      return fetch(getUrlWithOperation(url as URL, options?.body), options);
    } catch (error) {
      if (!isProduction()) {
        logger.error(error, `apolloUtils.tsx httpLink error: ${error.message}`);
      }
      throw error;
    }
  },
});

const ssrHttpLink = (accessToken?: string, refreshToken?: string) =>
  new HttpLink({
    uri: graphqlUrlInternal,
    fetchOptions: { cache: "no-store" },
    fetch: async (url, options) => {
      // Forward the real visitor IP + HMAC attestation so the GraphQL rate
      // limiter buckets SSR requests by the actual visitor, not the pod IP.
      const { clientIp, attestation } = await resolveSsrAttestation();

      try {
        return await fetch(getUrlWithOperation(url as URL, options?.body), {
          ...options,
          headers: {
            ...(accessToken && {
              [HEADER_KEYS.AUTHORIZATION]: `Bearer ${accessToken}`,
            }),
            ...(refreshToken && {
              [HEADER_KEYS.REFRESH_TOKEN]: refreshToken,
            }),
            ...(clientIp && { "x-real-ip": clientIp }),
            ...(attestation && { [HEADER_KEYS.SSR_ATTESTATION]: attestation }),
            ...options?.headers,
          },
        });
      } catch (error) {
        if (!isProduction()) {
          logger.error(error, `apolloUtils.tsx ssrHttpLink error: ${error.message}`);
        }
        throw error;
      }
    },
  });

const clientAuthLink = new SetContextLink(({ headers }) => {
  return {
    headers: {
      ...headers,
      [HEADER_KEYS.HREF]: window.location.href,
      // A browser with no GPC signal has not signaled a GPC opt-out. Server callers
      // still omit this header, and Sec-GPC: 1 takes precedence on the server.
      [HEADER_KEYS.GPC]: String(
        (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
      ),
    },
  };
});

const notAuthorizedToast = () =>
  errorToast(
    <div style={{ flexDirection: "column", alignItems: "center" }}>
      <p>You are not authorized to complete this action.</p>
      <p>
        If you believe this is a mistake, please reach out to{" "}
        <a
          href={externalUrls.MailToEmail}
          target="_blank"
          rel="noreferrer"
          style={{ color: "white" }}
        >
          support@dreambound.com
        </a>
      </p>
    </div>
  );

type Entity = StoreObject | Reference;
export const useApolloCache = () => {
  const { cache } = useApolloClient();

  const evictRootQuery = <FieldName extends keyof GQL.Query>(
    fieldName: FieldName,
    args?: Cache.EvictOptions["args"]
  ) => {
    cache.evict({ id: "ROOT_QUERY", fieldName, args });
    cache.gc();
  };

  const evictRootQueries = <FieldName extends keyof GQL.Query>(fieldNameOrNames: FieldName[]) => {
    fieldNameOrNames.map((fieldName) =>
      cache.evict({
        id: "ROOT_QUERY",
        fieldName,
      })
    );
    cache.gc();
  };

  const evictObject = (entity: Entity | null | undefined) => {
    if (entity) {
      cache.evict({ id: cache.identify(entity) });
      cache.gc();
    }
  };

  const evictObjects = (entities: (Entity | null)[] | null) => {
    entities?.map((entity) => entity && evictObject({ id: cache.identify(entity) }));
  };

  return {
    evictRootQuery,
    evictRootQueries,
    evictObject,
    evictObjects,
  };
};

const updateTokens = ({
  accessToken,
  refreshToken,
}: {
  accessToken?: string;
  refreshToken?: string;
}) => {
  if (accessToken && decodeJwt(accessToken)) {
    accessTokenVar(accessToken);
    setCookie(COOKIE_KEYS.ACCESS_TOKEN, accessToken);
  }
  if (refreshToken && decodeJwt(refreshToken)) {
    refreshTokenVar(refreshToken);
    setCookie(COOKIE_KEYS.REFRESH_TOKEN, refreshToken);
  }
};

const createApolloCache = () =>
  new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          usersByProvider: {
            merge(_, incoming) {
              return incoming;
            },
          },
        },
      },
      ClientAuthToken: {
        fields: {
          accessToken: {
            merge(_existing, incoming) {
              updateTokens({ accessToken: incoming });
              return incoming;
            },
          },
          refreshToken: {
            merge(_existing, incoming) {
              updateTokens({ refreshToken: incoming });
              return incoming;
            },
          },
        },
      },
    },
  });

export const logout = async () => {
  await serverLogout();
  window.location.reload();
};

export const logoutAndGoToLoginPage = async () => {
  await logout();
  window.location.href = getUrls().Login();
};

const errorLink = new ErrorLink(({ error }) => {
  const graphQLErrors = CombinedGraphQLErrors.is(error) ? error.errors : undefined;
  let errorHandled = true;
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      const clientError = detectKnownClientError(err);
      if (clientError === ClientFacingErrors.NotAuthorized) {
        notAuthorizedToast();
      } else if (
        (clientError === ClientFacingErrors.NotFound || err.message === "NotFoundError") &&
        err.path?.includes("session")
      ) {
        deleteCookie(COOKIE_KEYS.ACCESS_TOKEN);
        deleteCookie(COOKIE_KEYS.REFRESH_TOKEN);
        accessTokenVar(undefined);
        refreshTokenVar(undefined);
      } else if (
        clientError === ClientFacingErrors.CustomerDoesNotExist ||
        ((clientError === ClientFacingErrors.NotFound || err.message === "NotFoundError") &&
          err.path?.includes("viewerCustomer"))
      ) {
        // Stale-session signal after a customer merge: the JWT references a
        // soft-deleted source customer. The server-side tokenRefreshPlugin
        // refreshes the cookie on the merge response, but in-flight requests
        // can still arrive with the old cookie. Clear it so middleware mints
        // a fresh session customer on the next request.
        deleteCookie(COOKIE_KEYS.ACCESS_TOKEN);
        deleteCookie(COOKIE_KEYS.REFRESH_TOKEN);
        accessTokenVar(undefined);
        refreshTokenVar(undefined);
      } else if (!clientError || clientError === ClientFacingErrors.Unexpected) {
        /** If any errors are not clientErrors, show a toast **/
        errorHandled = false;
      }
    }
    if (!errorHandled) {
      errorToast("Something went wrong. Please contact Dreambound support.");
    }
  }
});

export const makeClient = (accessToken?: string, refreshToken?: string) =>
  new ApolloClient({
    link:
      typeof window !== "undefined"
        ? ApolloLink.from([
            sentryGraphqlHelperLink,
            errorLink,
            clientAuthLink,
            responseLink,
            httpLink,
          ])
        : ApolloLink.from([
            sentryGraphqlInternalHelperLink,
            ssrHttpLink(accessToken, refreshToken),
          ]),
    clientAwareness: {
      name: typeof window !== "undefined" ? "portal" : "portal-ssr",
      version: process.env.DREAM_ACTIVE_VERSION,
    },
    cache: createApolloCache(),
    // Keep regular-query refetch behavior. Lazy hooks explicitly opt in to
    // notifications: v4 otherwise suppresses even their initial loading state.
    defaultOptions: { watchQuery: { notifyOnNetworkStatusChange: false } },
    devtools: { enabled: process.env.NODE_ENV !== "production" },
  });
